import React, { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, IconButton, Modal } from "@mui/material";
import { toast } from "react-toastify";
import RefreshIcon from "@mui/icons-material/Refresh";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import "../../SalesActivity/sales.css";

const STATUS = {
  scheduled: { bg: "#eff6ff", color: "#3b82f6" },
  completed: { bg: "#f0fdf4", color: "#16a34a" },
  cancelled: { bg: "#fef2f2", color: "#dc2626" },
};

const CACHE_KEY = "zef_admin_demos_cache";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  maxWidth: "90%",
  borderRadius: "12px",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 0,
  overflow: "hidden",
};

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function readLocalCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

function writeLocalCache(data, meta) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, meta }));
  } catch (err) {
    // ignore (e.g. private browsing / storage disabled)
  }
}

export default function DemoActivity() {
  const navigate = useNavigate();
  const [demos, setDemos] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedRange, setAppliedRange] = useState({ from: "", to: "" });
  const [noteModalDemo, setNoteModalDemo] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const openNoteModal = (demo) => {
    setNoteModalDemo(demo);
    setNoteDraft(demo.note || "");
  };

  const closeNoteModal = () => {
    if (savingNote) return;
    setNoteModalDemo(null);
    setNoteDraft("");
  };

  const saveNote = async () => {
    const demoId = noteModalDemo.id;
    try {
      setSavingNote(true);
      const { data } = await Axios({
        method: "post",
        url: `/api/activity/demos/${demoId}/note`,
        data: { note: noteDraft },
      });
      setDemos((prev) =>
        prev.map((d) => (d.id === demoId ? { ...d, note: data.data.note } : d))
      );
      toast.success("Note saved");
      setNoteModalDemo(null);
      setNoteDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const fetchDemos = async ({ force, from, to } = {}) => {
    const hasCustomRange = Boolean(from || to);
    try {
      if (force) setRefreshing(true);
      setError(null);
      const params = new URLSearchParams();
      if (force) params.append("refresh", "true");
      if (from) params.append("startDate", from);
      if (to) params.append("endDate", to);
      const queryString = params.toString();
      const { data } = await Axios({
        method: "get",
        url: `/api/activity/demos${queryString ? `?${queryString}` : ""}`,
      });
      setDemos(data.data);
      setMeta(data.meta || null);
      setRateLimited(false);
      setRetryAfter(null);
      if (!hasCustomRange) {
        writeLocalCache(data.data, data.meta || null);
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        setRateLimited(true);
        setRetryAfter(err?.response?.data?.retryAfter ?? null);
        setDemos((prev) => (prev === null ? [] : prev));
      } else {
        setError(err?.response?.data?.error || "Failed to load demo meetings");
        setDemos((prev) => (prev === null ? [] : prev));
      }
    } finally {
      if (force) setRefreshing(false);
    }
  };

  useEffect(() => {
    const cached = readLocalCache();
    if (cached) {
      setDemos(cached.data);
      setMeta({ ...(cached.meta || {}), cached: true });
    }
    fetchDemos();
  }, []);

  const applyDateFilter = () => {
    setAppliedRange({ from: fromDate, to: toDate });
    fetchDemos({ force: true, from: fromDate, to: toDate });
  };

  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
    setAppliedRange({ from: "", to: "" });
    fetchDemos();
  };

  if (demos === null) {
    return <CircularProgress />;
  }

  if (rateLimited && demos.length === 0) {
    return (
      <div className="container admin-user-activity">
        <h3>Demo Meetings</h3>
        <p style={{ color: "#dc2626" }}>
          Calendly rate limit reached. Try again in{" "}
          {retryAfter ? `${retryAfter}s` : "a few seconds"}.
        </p>
      </div>
    );
  }

  const filtered =
    status === "All" ? demos : demos.filter((d) => d.status === status);

  return (
    <div className="container admin-user-activity">
      <h3></h3>
      <p style={{ color: "#6b7280", fontSize: 12, marginTop: -8 }}>
        {appliedRange.from || appliedRange.to
          ? `Showing demos ${
              appliedRange.from ? `from ${appliedRange.from} ` : ""
            }${appliedRange.to ? `to ${appliedRange.to}` : "onward"}.`
          : "Showing demos from the last 30 days plus all upcoming bookings."}
      </p>
      <div className="">
        <p htmlFor="statusDropdown">Select status</p>
        <select
          id="statusDropdown"
          className="h-40px"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="All">All</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <span style={{ marginLeft: 12 }}>
          <label htmlFor="fromDate" style={{ marginRight: 4 }}>
            From
          </label>
          <input
            id="fromDate"
            type="date"
            className="h-40px"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </span>
        <span style={{ marginLeft: 8 }}>
          <label htmlFor="toDate" style={{ marginRight: 4 }}>
            To
          </label>
          <input
            id="toDate"
            type="date"
            className="h-40px"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)}
          />
        </span>
        <button
          className="pointer"
          onClick={applyDateFilter}
          disabled={
            refreshing ||
            (!fromDate && !toDate) ||
            (fromDate === appliedRange.from && toDate === appliedRange.to)
          }
          style={{ marginLeft: 8 }}
        >
          Apply
        </button>
        {(appliedRange.from || appliedRange.to) && (
          <button
            className="pointer"
            onClick={clearDateFilter}
            disabled={refreshing}
            style={{ marginLeft: 8 }}
          >
            Clear dates
          </button>
        )}
        ({filtered.length})
        <button
          className="pointer"
          onClick={() =>
            fetchDemos({ force: true, from: appliedRange.from, to: appliedRange.to })
          }
          disabled={refreshing}
          style={{ marginLeft: 12 }}
        >
          <RefreshIcon fontSize="small" /> {refreshing ? "Refreshing..." : "Refresh"}
        </button>
        {meta?.fetchedAt && (
          <span style={{ marginLeft: 12, color: "#6b7280", fontSize: 12 }}>
            Last updated: {formatDateTime(meta.fetchedAt)}
          </span>
        )}
      </div>
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {(meta?.rateLimited || rateLimited) && (
        <p style={{ color: "#d97706" }}>
          Calendly rate limit reached — showing cached results
          {meta?.fetchedAt ? ` from ${formatDateTime(meta.fetchedAt)}` : ""}.
        </p>
      )}
      <table>
        <tr>
          <th>Attendee</th>
          <th>Email</th>
          <th>Event Type</th>
          <th>Date & Time</th>
          <th>Status</th>
          <th>Meeting Link</th>
          <th>Notes</th>
        </tr>
        {filtered.map((demo) => {
          const primary = demo.attendees && demo.attendees[0];
          const st = STATUS[demo.status] || { bg: "#f3f4f6", color: "#374151" };
          const isCompleted = demo.status === "completed";
          return (
            <tr key={demo.id} className="my-2 pt-2">
              <td className="pt-2">
                {primary ? primary.name : "—"}
                {demo.attendees && demo.attendees.length > 1
                  ? ` (+${demo.attendees.length - 1} more)`
                  : ""}
                {primary && (
                  primary.platformUser ? (
                    <span
                      className="stripe-badge pointer"
                      style={{ background: "#f0fdf4", color: "#16a34a", marginLeft: 8 }}
                      onClick={() =>
                        navigate(`/admin/user-activity?tab=13&userid=${primary.platformUser._id}`)
                      }
                    >
                      Platform User
                    </span>
                  ) : (
                    <span
                      className="stripe-badge"
                      style={{ background: "#f3f4f6", color: "#374151", marginLeft: 8 }}
                    >
                      Not Signed Up
                    </span>
                  )
                )}
              </td>
              <td className="pt-2">{primary ? primary.email : "—"}</td>
              <td className="pt-2">{demo.eventType}</td>
              <td className="pt-2">{formatDateTime(demo.startTime)}</td>
              <td className="pt-2">
                <span
                  className="stripe-badge"
                  style={{ background: st.bg, color: st.color }}
                >
                  {demo.status}
                </span>
              </td>
              <td className="pt-2">
                {demo.meetingLink ? (
                  <a href={demo.meetingLink} target="_blank" rel="noreferrer">
                    Join
                  </a>
                ) : (
                  "—"
                )}
              </td>
              <td className="pt-2" style={{ maxWidth: 260 }}>
                {!isCompleted ? (
                  <span style={{ color: "#9ca3af" }}>—</span>
                ) : (
                  <div
                    className="pointer"
                    onClick={() => openNoteModal(demo)}
                    style={{ display: "flex", alignItems: "flex-start", gap: 6 }}
                    title={demo.note ? "Edit note" : "Add note"}
                  >
                    {demo.note ? (
                      <>
                        <NoteAltOutlinedIcon
                          fontSize="small"
                          style={{ color: "#6b7280", flexShrink: 0, marginTop: 1 }}
                        />
                        <span
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            color: "#374151",
                            fontSize: 13,
                          }}
                        >
                          {demo.note}
                        </span>
                      </>
                    ) : (
                      <span
                        className="stripe-badge"
                        style={{ background: "#eff6ff", color: "#3b82f6", display: "inline-flex", alignItems: "center", gap: 4 }}
                      >
                        <NoteAddOutlinedIcon style={{ fontSize: 14 }} /> Add note
                      </span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </table>

      <Modal open={!!noteModalDemo} onClose={closeNoteModal}>
        <Box sx={modalStyle}>
          {noteModalDemo && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                    Meeting Notes
                  </p>
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 12 }}>
                    {(noteModalDemo.attendees && noteModalDemo.attendees[0]?.name) || "—"}
                    {" · "}
                    {formatDateTime(noteModalDemo.startTime)}
                  </p>
                </div>
                <IconButton size="small" onClick={closeNoteModal} disabled={savingNote}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>

              <div style={{ padding: 20 }}>
                <textarea
                  autoFocus
                  rows={6}
                  style={{
                    width: "100%",
                    resize: "vertical",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    padding: 10,
                    fontFamily: "inherit",
                    fontSize: 13.5,
                    boxSizing: "border-box",
                  }}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Summarize what was discussed, next steps, follow-ups..."
                  disabled={savingNote}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  padding: "12px 20px",
                  borderTop: "1px solid #f3f4f6",
                }}
              >
                <button className="pointer" onClick={closeNoteModal} disabled={savingNote}>
                  Cancel
                </button>
                <button className="pointer" onClick={saveNote} disabled={savingNote}>
                  {savingNote ? "Saving..." : "Save Note"}
                </button>
              </div>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
}
